import type {
  GoPhotoUploadResponse,
  SetGoPhotoCodeResponse,
} from '@/shared/types/api';
import {
  api,
  callBreadcrumbs,
  instrument,
  requireSessionId,
  unwrap,
} from '../client';

export function setGophotoCode(
  gophotoCode: string
): Promise<SetGoPhotoCodeResponse> {
  return instrument<SetGoPhotoCodeResponse>(
    {
      span: 'api.set_gophoto_code',
      errorTag: 'gophoto_set_gophoto_code',
      tags: { 'gophoto.code_length': gophotoCode?.length },
      ...callBreadcrumbs(
        'Setting GoPhoto code',
        'GoPhoto code set successfully',
        { code_length: gophotoCode?.length }
      ),
      trackErrorCode: 'api_error_set_gophoto_code',
    },
    () => {
      const sessionId = requireSessionId();
      return unwrap(
        api.PUT('/v1/sessions/{session_id}/gophoto-code', {
          params: { path: { session_id: sessionId } },
          body: { gophoto_code: gophotoCode.toUpperCase() },
        })
      );
    }
  );
}

export type GoPhotoUploadOptions = {
  /**
   * Fire-and-forget mode (THE-210). When true the backend schedules the
   * upload as an asyncio task, returns 202 immediately, and runs the
   * per-image retry loop server-side. The response's `results` is
   * empty and `accepted: true`; the visitor sees a success screen
   * within a second of confirming, regardless of how long the actual
   * upload takes.
   */
  background?: boolean;
};

export function uploadToGophoto(
  imageUuids: string[],
  gophotoCode: string,
  options: GoPhotoUploadOptions = {}
): Promise<GoPhotoUploadResponse> {
  const imageCount = imageUuids?.length ?? 0;
  const background = options.background === true;
  return instrument<GoPhotoUploadResponse>(
    {
      span: 'api.upload_to_gophoto',
      errorTag: 'gophoto_create_gophoto_uploads',
      tags: { 'api.image_count': imageCount, 'api.background': background },
      before: {
        message: background
          ? 'Scheduling GoPhoto upload (background)'
          : 'Uploading to GoPhoto',
        data: {
          image_count: imageCount,
          // Keep the full GoPhoto code in Sentry. It is the support lookup key
          // shared by the kiosk, backend rows, and the external GoPhoto portal.
          gophoto_code: gophotoCode,
          background,
        },
      },
      after: () => ({
        message: background
          ? 'GoPhoto upload accepted (background)'
          : 'GoPhoto upload completed',
        data: { image_count: imageCount, success: true, background },
      }),
      trackErrorCode: 'api_error_gophoto_upload',
    },
    () => {
      const sessionId = requireSessionId();
      return unwrap(
        api.POST('/v1/sessions/{session_id}/gophoto-uploads', {
          params: { path: { session_id: sessionId } },
          body: {
            image_uuids: imageUuids,
            gophoto_code: gophotoCode.toUpperCase(),
            background,
          },
        })
      );
    }
  );
}
